import {
  BLACK,
  darken,
  fade,
  hex,
  lighten,
  mixColor,
  withAlpha,
  type Color,
} from "../../common/color.js";
import type { Point, Rect } from "../../common/geometry.js";
import {
  GRADIENT_DIAGONAL,
  GRADIENT_DOWN,
  GRADIENT_LEFT,
  GRADIENT_RIGHT,
  GRADIENT_UP,
  type RenderContext,
} from "../render/RenderContext.js";
import { bevelEdge } from "../themes/ClassicTheme.js";
import { lookShape } from "../art/looks.js";
import { strokeTriangles } from "../art/markup/shapes.js";
import type { Theme, VisualState } from "../themes/Theme.js";
import {
  bool,
  choice,
  color,
  Flare,
  num,
  registerFlare,
  type FlareContext,
  type FlareSpec,
} from "./Flare.js";

/** Smooth 0..1..0 over one period; the shape most pulses want. */
function pulseWave(time: number, period: number): number {
  if (period <= 0) return 1;
  return 0.5 - 0.5 * Math.cos((time / period) * Math.PI * 2);
}

function grow(r: Rect, by: number): Rect {
  return { x: r.x - by, y: r.y - by, width: r.width + by * 2, height: r.height + by * 2 };
}

/** Deterministic pseudo-random in 0..1, so sparkles do not jitter per frame. */
function hashUnit(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

// --- perimeter walking --------------------------------------------------
// Three effects place things along the edge of a control. Doing it by arc
// length rather than per side is what lets a dash or a dot cross a corner
// without restarting there.

function perimeter(r: Rect): number {
  return 2 * (r.width + r.height);
}

/**
 * The point `dist` px along the perimeter, clockwise from the top-left
 * corner. Distances outside the perimeter wrap, in both directions.
 */
function pointOnPerimeter(r: Rect, dist: number): Point {
  const total = perimeter(r);
  if (total <= 0) return { x: r.x, y: r.y };
  let t = dist % total;
  if (t < 0) t += total;
  if (t < r.width) return { x: r.x + t, y: r.y };
  t -= r.width;
  if (t < r.height) return { x: r.x + r.width, y: r.y + t };
  t -= r.height;
  if (t < r.width) return { x: r.x + r.width - t, y: r.y + r.height };
  return { x: r.x, y: r.y + r.height - (t - r.width) };
}

/**
 * Fill the run of the perimeter between two arc lengths, as one axis-aligned
 * rect per side it crosses. Arc lengths outside the perimeter are clamped
 * rather than wrapped, so a caller can hand in a run that overhangs an end.
 */
function strokePerimeterRun(
  rc: RenderContext,
  r: Rect,
  from: number,
  to: number,
  thickness: number,
  ink: Color,
): void {
  const half = thickness / 2;
  const { width: w, height: h } = r;
  // Each side: where it starts along the perimeter, how long it is, and how
  // to turn a span of that side into a rect. Order matches pointOnPerimeter.
  const side = (at: number, len: number, place: (a: number, b: number) => Rect): void => {
    const s = Math.max(from, at);
    const e = Math.min(to, at + len);
    if (e > s) rc.fillRect(place(s - at, e - at), ink);
  };
  side(0, w, (a, b) => ({ x: r.x + a, y: r.y - half, width: b - a, height: thickness }));
  side(w, h, (a, b) => ({ x: r.x + w - half, y: r.y + a, width: thickness, height: b - a }));
  side(w + h, w, (a, b) => ({
    x: r.x + w - b,
    y: r.y + h - half,
    width: b - a,
    height: thickness,
  }));
  side(2 * w + h, h, (a, b) => ({
    x: r.x - half,
    y: r.y + h - b,
    width: thickness,
    height: b - a,
  }));
}

/** Smooth value noise in 0..1: a hashed lattice with a smoothstep between. */
function noise1(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const t = f * f * (3 - 2 * f);
  return hashUnit(i) * (1 - t) + hashUnit(i + 1) * t;
}

/**
 * A fully saturated color at hue `h` (in turns, wrapping).
 *
 * The palette has no hue ramp — themes deal in named roles, not wheels — and
 * only the rainbow effects want one, so it lives here.
 */
function hueRgb(h: number): Color {
  const t = ((h % 1) + 1) % 1;
  const k = (n: number): number => {
    const x = (n + t * 6) % 6;
    return Math.max(0, Math.min(1, Math.min(x, 4 - x)));
  };
  return { r: k(5), g: k(3), b: k(1), a: 1 };
}

// --- bases --------------------------------------------------------------
// Three patterns recur across the effects, and each of them exists because
// `animated` is asked outside a frame, where there is no context to read.

/**
 * Base for effects that can be gated on hover.
 *
 * The gate has to be *remembered*: an effect that only runs while hovered
 * must also stop asking for frames when the pointer leaves, and `animated`
 * has no state to look at. `update` runs on every flare every frame, so the
 * last value it saw is the answer. Subclasses that override `update` must
 * call `super.update`.
 */
export abstract class HoverGatedFlare extends Flare {
  /** Only run while the control is hovered. */
  onHover: boolean;

  #hovered = false;

  constructor(onHover = false) {
    super();
    this.onHover = onHover;
  }

  /**
   * True when the gate is shut and the effect should paint nothing. Reads the
   * frame being painted rather than the remembered state, so an effect drawn
   * without an `update` first still behaves.
   */
  protected gated(ctx: FlareContext): boolean {
    return this.onHover && !ctx.state.hovered;
  }

  /** True while the gate is open, judged on the last frame seen. */
  protected get running(): boolean {
    return !this.onHover || this.#hovered;
  }

  override get animated(): boolean {
    return this.running;
  }

  override update(ctx: FlareContext): void {
    this.#hovered = ctx.state.hovered;
  }
}

/** The visual state a `StateFlare` follows. */
export type StateTrigger = "hover" | "press" | "focus" | "disabled" | "always";

const STATE_TRIGGERS: readonly StateTrigger[] = [
  "hover",
  "press",
  "focus",
  "disabled",
  "always",
];

function stateHolds(when: StateTrigger, state: VisualState): boolean {
  switch (when) {
    case "hover":
      return state.hovered;
    case "press":
      return state.pressed;
    case "focus":
      return state.focused;
    case "disabled":
      return state.disabled;
    case "always":
      return true;
  }
}

/**
 * Base for effects that follow a visual state, easing between off and on.
 *
 * The only effects driven by `delta` rather than `time`: they hold a level,
 * walk it towards the target each frame, and report `animated` only while it
 * is between the two — so a settled effect costs the desktop nothing.
 */
export abstract class StateFlare extends Flare {
  when: StateTrigger;
  /** Seconds to ease in and out. 0 snaps. */
  fade: number;

  #level = 0;

  constructor(when: StateTrigger = "hover", fade = 0.14) {
    super();
    this.when = when;
    this.fade = fade;
  }

  /** Current strength, 0..1. */
  get level(): number {
    return this.#level;
  }

  override get animated(): boolean {
    return this.#level > 0 && this.#level < 1;
  }

  override update(ctx: FlareContext): void {
    const target = stateHolds(this.when, ctx.state) ? 1 : 0;
    if (this.fade <= 0) {
      this.#level = target;
      return;
    }
    const step = ctx.delta / this.fade;
    this.#level =
      target > this.#level
        ? Math.min(target, this.#level + step)
        : Math.max(target, this.#level - step);
  }
}

/** What a `FiredFlare` needs of the control it listens to. */
interface Clickable {
  on: (e: "click", h: () => void) => void;
  off: (e: "click", h: () => void) => void;
}

/**
 * Base for effects that fire, play once and retire: a ring, a starburst.
 *
 * What sets these apart is not what they draw but that they stop. The list of
 * shots in flight lives here along with the click wiring, so `animated` can
 * go false when the last one finishes and let the desktop idle again.
 */
export abstract class FiredFlare extends Flare {
  /** Seconds one shot takes. */
  duration: number;
  /** Fire automatically whenever the control is clicked. */
  onClick: boolean;

  /** Start times of the shots in flight; NaN until the next frame stamps it. */
  #starts: number[] = [];
  #detach: (() => void) | null = null;

  constructor(duration: number, onClick: boolean) {
    super();
    this.duration = duration;
    this.onClick = onClick;
  }

  override onAttach(control: Clickable): void {
    if (!this.onClick) return;
    const handler = (): void => this.fire();
    control.on("click", handler);
    this.#detach = () => control.off("click", handler);
  }

  override onDetach(): void {
    this.#detach?.();
    this.#detach = null;
  }

  /**
   * Start one on the next frame.
   *
   * Deliberately not stamped with the last frame's time: the flare clock only
   * moves when the desktop paints, and on an idle desktop the last frame can
   * be minutes back — a shot stamped with it would be retired before it ever
   * drew. Clicking a button on a still screen is exactly that case.
   */
  fire(): void {
    this.#starts.push(Number.NaN);
  }

  override get animated(): boolean {
    return this.#starts.length > 0;
  }

  override update(ctx: FlareContext): void {
    // Stamp anything fired since the last frame, then retire what has run its
    // course so `animated` can go false and the desktop idle again.
    for (let i = 0; i < this.#starts.length; i++) {
      if (Number.isNaN(this.#starts[i])) this.#starts[i] = ctx.time;
    }
    this.#starts = this.#starts.filter((start) => ctx.time - start < this.duration);
  }

  /** Progress 0..1 of each shot in flight, oldest first. */
  protected shots(time: number): number[] {
    const out: number[] = [];
    for (const start of this.#starts) {
      // Fired but not yet stamped: it belongs to the frame after this one.
      if (Number.isNaN(start)) continue;
      out.push(Math.min(1, Math.max(0, (time - start) / this.duration)));
    }
    return out;
  }
}

// --- outline ------------------------------------------------------------

export interface OutlineFlareOptions {
  color?: Color;
  width?: number;
  /** Positive pushes the ring outside the control, negative pulls it in. */
  offset?: number;
  radius?: number;
  /** Breathe the opacity, in seconds per cycle. 0 is steady. */
  pulse?: number;
}

/** A ring drawn around the control, optionally breathing. */
export class OutlineFlare extends Flare {
  color: Color | null;
  width: number;
  offset: number;
  radius: number;
  pulse: number;

  constructor(options: OutlineFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.width = options.width ?? 2;
    this.offset = options.offset ?? 2;
    this.radius = options.radius ?? -1;
    this.pulse = options.pulse ?? 0;
  }

  override get overflow(): number {
    return Math.max(0, this.offset) + this.width;
  }

  override get animated(): boolean {
    return this.pulse > 0;
  }

  override paintAbove(ctx: FlareContext): void {
    const base = this.color ?? ctx.theme.palette.accent;
    const alpha = this.pulse > 0 ? 0.35 + 0.65 * pulseWave(ctx.time, this.pulse) : 1;
    const r = grow(ctx.bounds, this.offset);
    const radius =
      this.radius >= 0 ? this.radius : ctx.theme.metrics.controlCornerRadius + Math.max(0, this.offset);
    ctx.rc.strokeRect(r, fade(base, base.a * alpha), this.width, radius);
  }
}

// --- shadow -------------------------------------------------------------

export interface ShadowFlareOptions {
  color?: Color;
  offsetX?: number;
  offsetY?: number;
  /** How far the shadow spreads past the control. */
  blur?: number;
  radius?: number;
}

/**
 * A soft drop shadow.
 *
 * There is no blur pass in the renderer, so the falloff is a handful of
 * concentric rounded rects at decreasing opacity — a few quads, and at these
 * radii the banding is invisible.
 */
export class ShadowFlare extends Flare {
  color: Color | null;
  offsetX: number;
  offsetY: number;
  blur: number;
  radius: number;
  steps = 5;

  constructor(options: ShadowFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.offsetX = options.offsetX ?? 0;
    this.offsetY = options.offsetY ?? 3;
    this.blur = options.blur ?? 8;
    this.radius = options.radius ?? -1;
  }

  override get overflow(): number {
    return this.blur + Math.max(Math.abs(this.offsetX), Math.abs(this.offsetY));
  }

  override paintBelow(ctx: FlareContext): void {
    const base = this.color ?? ctx.theme.palette.shadow;
    if (base.a <= 0) return;
    const radius = this.radius >= 0 ? this.radius : ctx.theme.metrics.controlCornerRadius;
    for (let i = this.steps; i >= 1; i--) {
      const spread = (this.blur * i) / this.steps;
      const r = grow(ctx.bounds, spread);
      ctx.rc.fillRect(
        { ...r, x: r.x + this.offsetX, y: r.y + this.offsetY },
        fade(base, (base.a * 0.5) / i),
        radius + spread,
      );
    }
  }
}

// --- glow ---------------------------------------------------------------

export interface GlowFlareOptions {
  color?: Color;
  /** How far the glow reaches. */
  spread?: number;
  strength?: number;
  radius?: number;
  /** Seconds per breath. 0 is steady. */
  pulse?: number;
  /** Only glow while the control is hovered. */
  onHover?: boolean;
}

/** A colored halo behind the control. */
export class GlowFlare extends HoverGatedFlare {
  color: Color | null;
  spread: number;
  strength: number;
  radius: number;
  pulse: number;
  steps = 4;

  constructor(options: GlowFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.spread = options.spread ?? 8;
    this.strength = options.strength ?? 1;
    this.radius = options.radius ?? -1;
    this.pulse = options.pulse ?? 0;
  }

  override get overflow(): number {
    return this.spread;
  }

  override get animated(): boolean {
    return this.pulse > 0 && this.running;
  }

  override paintBelow(ctx: FlareContext): void {
    if (this.gated(ctx)) return;
    const base = this.color ?? ctx.theme.palette.accent;
    const wave = this.pulse > 0 ? 0.4 + 0.6 * pulseWave(ctx.time, this.pulse) : 1;
    // The halo follows the shape the control actually paints: a medallion's circle, a plate's
    // corners. A look that does not say, and a control wearing none, glow round their bounds.
    const color = fade(base, Math.min(1, base.a * 0.55 * this.strength * wave));
    // A control with a shape of its own glows round its outline: rings of stroke, widest faintest.
    const rings = (ctx.control as { shapeOutline?: () => Point[][] | null } | null)?.shapeOutline?.();
    if (rings && rings.length > 0) {
      const ox = ctx.bounds.x;
      const oy = ctx.bounds.y;
      const placed = rings.map((r) => r.map((p) => ({ x: p.x + ox, y: p.y + oy })));
      for (let k = 4; k >= 1; k--) {
        const ink = fade(color, 0.35 / k);
        for (const ring of placed) ctx.rc.fillTriangles(strokeTriangles(ring, true, (this.spread * k) / 2), ink);
      }
      return;
    }
    const shape = lookShape(ctx.control, ctx.bounds);
    const radius = this.radius >= 0 ? this.radius : (shape?.radius ?? ctx.theme.metrics.controlCornerRadius);
    ctx.rc.fillGlow(
      shape?.rect ?? ctx.bounds,
      fade(base, Math.min(1, base.a * 0.55 * this.strength * wave)),
      this.spread,
      radius,
    );
  }
}

// --- gradient -----------------------------------------------------------

export type GradientDirection = "down" | "up" | "right" | "left" | "diagonal";

const AXES: Record<GradientDirection, readonly [number, number, number, number]> = {
  down: GRADIENT_DOWN,
  up: GRADIENT_UP,
  right: GRADIENT_RIGHT,
  left: GRADIENT_LEFT,
  diagonal: GRADIENT_DIAGONAL,
};

const GRADIENT_DIRECTIONS = Object.keys(AXES) as GradientDirection[];

export interface GradientFlareOptions {
  from?: Color;
  to?: Color;
  direction?: GradientDirection;
  radius?: number;
  /** Slide the ramp back and forth, seconds per cycle. 0 is steady. */
  shift?: number;
  /** Paint over the control instead of under it, as a wash. */
  above?: boolean;
  /** Opacity when painted above. */
  opacity?: number;
}

/**
 * A linear gradient fill.
 *
 * One quad: the fragment shader interpolates between the two stops, so there
 * is no banding however large the control gets.
 */
export class GradientFlare extends Flare {
  from: Color | null;
  to: Color | null;
  direction: GradientDirection;
  radius: number;
  shift: number;
  above: boolean;
  opacity: number;

  constructor(options: GradientFlareOptions = {}) {
    super();
    this.from = options.from ?? null;
    this.to = options.to ?? null;
    this.direction = options.direction ?? "down";
    this.radius = options.radius ?? -1;
    this.shift = options.shift ?? 0;
    this.above = options.above ?? false;
    this.opacity = options.opacity ?? 1;
  }

  override get animated(): boolean {
    return this.shift > 0;
  }

  #paint(ctx: FlareContext): void {
    const p = ctx.theme.palette;
    const from = this.from ?? p.accent;
    const to = this.to ?? lighten(p.accent, 0.35);
    const radius = this.radius >= 0 ? this.radius : ctx.theme.metrics.controlCornerRadius;
    let axis = AXES[this.direction];
    if (this.shift > 0) {
      // Slide the ramp along its own axis, wrapping smoothly.
      const t = pulseWave(ctx.time, this.shift) - 0.5;
      axis = [axis[0] + t, axis[1] + t, axis[2] + t, axis[3] + t];
    }
    const alpha = this.above ? this.opacity : 1;
    ctx.rc.fillGradient(ctx.bounds, fade(from, from.a * alpha), fade(to, to.a * alpha), axis, radius);
  }

  override paintBelow(ctx: FlareContext): void {
    if (!this.above) this.#paint(ctx);
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.above) this.#paint(ctx);
  }
}

// --- sparkle ------------------------------------------------------------

export interface SparkleFlareOptions {
  color?: Color;
  count?: number;
  /** Seconds for one twinkle cycle. */
  period?: number;
  /** Largest arm length of a star, in px. */
  size?: number;
  /** Keep sparkles inside the control instead of around its edge. */
  inside?: boolean;
  /** Only sparkle while hovered. */
  onHover?: boolean;
  seed?: number;
}

/** What a highlight is made of on a dark theme, whatever it is highlighting. */
const HIGHLIGHT_ON_DARK = hex("#ffffff");

/**
 * The ink a highlight effect uses when it has not been given one.
 *
 * White is what a highlight looks like, and on the dark themes it is right.
 * On a light one it is invisible — white over a white window is not a subtle
 * effect, it is no effect — so light themes fall back to `onLight`, which each
 * effect picks for the surface it actually sits on.
 *
 * A fixed pair rather than a contrast computed at paint time, because a flare
 * does not know what is behind the control: sparkles can sit out over the
 * desktop, a sheen sweeps whatever fill the control happens to have, and there
 * is no single background to measure against.
 */
function highlightInk(theme: Theme, onLight: Color): Color {
  return theme.isDark ? HIGHLIGHT_ON_DARK : onLight;
}

/**
 * Little four-armed stars that twinkle around the control.
 *
 * Positions come from a hash of the sparkle index rather than a random number
 * generator, so they stay put frame to frame and the effect reads as
 * twinkling rather than static.
 */
export class SparkleFlare extends HoverGatedFlare {
  color: Color | null;
  count: number;
  period: number;
  size: number;
  inside: boolean;
  seed: number;

  constructor(options: SparkleFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.count = options.count ?? 8;
    this.period = options.period ?? 2.2;
    this.size = options.size ?? 5;
    this.inside = options.inside ?? false;
    this.seed = options.seed ?? 1;
  }

  override get overflow(): number {
    return this.inside ? 0 : this.size;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.gated(ctx)) return;
    const base = this.color ?? highlightInk(ctx.theme, ctx.theme.palette.accent);
    const b = ctx.bounds;

    for (let i = 0; i < this.count; i++) {
      const s = i * 7.3 + this.seed * 31.7;
      // Each sparkle runs its own phase so they do not blink in unison.
      const phase = hashUnit(s + 3.1);
      const wave = pulseWave(ctx.time + phase * this.period, this.period);
      if (wave < 0.02) continue;

      // Inside, anywhere on the face; outside, somewhere along the edge.
      const { x, y } = this.inside
        ? { x: b.x + hashUnit(s) * b.width, y: b.y + hashUnit(s + 1.7) * b.height }
        : pointOnPerimeter(b, hashUnit(s) * perimeter(b));

      const arm = this.size * wave;
      const ink = fade(base, base.a * wave);
      // A plus and a smaller diagonal core reads as a star at these sizes.
      ctx.rc.fillRect({ x: x - arm, y: y - 0.5, width: arm * 2, height: 1 }, ink);
      ctx.rc.fillRect({ x: x - 0.5, y: y - arm, width: 1, height: arm * 2 }, ink);
      const core = arm * 0.35;
      if (core >= 0.5) {
        ctx.rc.fillRect({ x: x - core, y: y - core, width: core * 2, height: core * 2 }, ink, core);
      }
    }
  }
}

// --- ripple -------------------------------------------------------------

export interface RippleFlareOptions {
  color?: Color;
  /** Seconds for one ring to travel and fade. */
  duration?: number;
  /** How far past the control the ring travels. */
  reach?: number;
  width?: number;
  /** Run it backwards: the ring closes in on the control instead. */
  inward?: boolean;
  /** Fire automatically whenever the control is clicked. */
  onClick?: boolean;
}

/**
 * A ring traveling out from the control, fired on demand or on every click.
 *
 * `inward` runs the same ring backwards — it starts out at `reach` and closes
 * onto the edge, fading *in* as it lands rather than out as it leaves. The
 * outward one reads as something leaving; the inward one as something
 * arriving, or a control gathering itself before it acts.
 *
 * Transient, like every `FiredFlare`: `animated` goes false once the last
 * ring finishes, which lets the desktop stop rendering every frame.
 */
export class RippleFlare extends FiredFlare {
  color: Color | null;
  reach: number;
  width: number;
  inward: boolean;

  constructor(options: RippleFlareOptions = {}) {
    super(options.duration ?? 0.45, options.onClick ?? true);
    this.color = options.color ?? null;
    this.reach = options.reach ?? 14;
    this.width = options.width ?? 2;
    this.inward = options.inward ?? false;
  }

  override get overflow(): number {
    return this.reach + this.width;
  }

  override paintAbove(ctx: FlareContext): void {
    const base = this.color ?? ctx.theme.palette.accent;
    const radius = ctx.theme.metrics.controlCornerRadius;
    for (const t of this.shots(ctx.time)) {
      // Same ring, read from the other end — but not the same fade. Going
      // out, opacity simply runs down with distance. Coming in, it has to be
      // up early (a ring nobody can see is not a gesture) and gone at the
      // very end, or it lands on the control's own border and sits there
      // looking like a mis-drawn outline.
      const spread = this.reach * (this.inward ? 1 - t : t);
      const alpha = this.inward ? Math.sqrt(t) * (t > 0.8 ? (1 - t) / 0.2 : 1) : 1 - t;
      ctx.rc.strokeRect(
        grow(ctx.bounds, spread),
        fade(base, base.a * alpha),
        this.width,
        radius + spread,
      );
    }
  }
}

// --- sheen --------------------------------------------------------------

export interface SheenFlareOptions {
  color?: Color;
  /** Band thickness in px. */
  width?: number;
  /** Seconds one sweep takes. */
  duration?: number;
  /** Seconds from the start of one sweep to the start of the next. */
  period?: number;
  /** Horizontal shift over the control's height; 0 is an upright bar. */
  slant?: number;
  opacity?: number;
  /** Only sweep while the control is hovered. */
  onHover?: boolean;
}

/**
 * A highlight that sweeps across the face — the glint a glossy button gets.
 *
 * The band is three stacked strokes, wide and dim under narrow and bright,
 * which reads as a soft-edged bar without a blur pass. It is clipped to the
 * control, so the round caps on those strokes never show.
 */
export class SheenFlare extends HoverGatedFlare {
  color: Color | null;
  width: number;
  duration: number;
  period: number;
  slant: number;
  opacity: number;

  constructor(options: SheenFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.width = options.width ?? 18;
    this.duration = options.duration ?? 0.7;
    this.period = options.period ?? 3.4;
    this.slant = options.slant ?? 0.4;
    this.opacity = options.opacity ?? 0.35;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.gated(ctx) || this.duration <= 0) return;
    const cycle = Math.max(this.period, this.duration);
    const t = (ctx.time % cycle) / this.duration;
    if (t > 1) return; // resting between sweeps

    const b = ctx.bounds;
    // Dark rather than the accent on a light theme: this sweeps the control's
    // own face, and that face is often accent-colored already — a glossy
    // primary button is the whole reason the effect exists — so an accent
    // sheen would disappear exactly where it is most wanted.
    const base = this.color ?? highlightInk(ctx.theme, ctx.theme.palette.text);
    const lean = this.slant * b.height;
    // Start clear of the leading edge and finish clear of the trailing one,
    // so the band enters and leaves rather than popping.
    const from = b.x - this.width - Math.abs(lean);
    const to = b.x + b.width + this.width + Math.abs(lean);
    const x = from + (to - from) * t;
    // Fade in and out across the sweep; a hard-edged bar reads as a glitch.
    const alpha = this.opacity * Math.sin(Math.PI * t);
    const top = { x: x + lean / 2, y: b.y - this.width };
    const bottom = { x: x - lean / 2, y: b.y + b.height + this.width };

    ctx.rc.clipped(b, () => {
      for (const [scale, weight] of SHEEN_LAYERS) {
        ctx.rc.drawLine(top, bottom, this.width * scale, fade(base, base.a * alpha * weight));
      }
    });
  }
}

/** Thickness and opacity of each pass of the sheen band, widest first. */
const SHEEN_LAYERS: readonly (readonly [number, number])[] = [
  [1, 0.4],
  [0.55, 0.6],
  [0.2, 1],
];

// --- marching ants ------------------------------------------------------

export interface AntsFlareOptions {
  color?: Color;
  /** Length of one dash, px. */
  dash?: number;
  /** Space between dashes, px. */
  gap?: number;
  thickness?: number;
  /** Px the ring sits outside the control. */
  offset?: number;
  /** Dash travel in px per second; negative marches the other way. */
  speed?: number;
}

/**
 * The dashed marquee that has meant "selected" since MacPaint.
 *
 * Dashes are placed by arc length around the whole perimeter rather than per
 * side, so they carry on around the corners instead of restarting at each.
 */
export class AntsFlare extends Flare {
  color: Color | null;
  dash: number;
  gap: number;
  thickness: number;
  offset: number;
  speed: number;

  constructor(options: AntsFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.dash = options.dash ?? 6;
    this.gap = options.gap ?? 4;
    this.thickness = options.thickness ?? 1;
    this.offset = options.offset ?? 2;
    this.speed = options.speed ?? 14;
  }

  override get overflow(): number {
    return Math.max(0, this.offset) + this.thickness;
  }

  override get animated(): boolean {
    return this.speed !== 0;
  }

  override paintAbove(ctx: FlareContext): void {
    const base = this.color ?? ctx.theme.palette.accent;
    const r = grow(ctx.bounds, this.offset);
    const total = perimeter(r);
    const step = Math.max(1, this.dash + this.gap);
    if (total <= 0 || this.dash <= 0) return;

    // Begin one step before the corner so the dash straddling the seam is
    // drawn as well; the halves that fall outside are clamped away.
    let phase = (ctx.time * this.speed) % step;
    if (phase > 0) phase -= step;
    for (let s = phase; s < total; s += step) {
      strokePerimeterRun(
        ctx.rc,
        r,
        Math.max(s, 0),
        Math.min(s + this.dash, total),
        this.thickness,
        base,
      );
    }
  }
}

// --- brackets -----------------------------------------------------------

export interface BracketsFlareOptions {
  color?: Color;
  /** Arm length of each corner, px. */
  size?: number;
  thickness?: number;
  /** Px outside the control the brackets sit. */
  offset?: number;
  /** Seconds per breath; both the gap and the opacity ease. 0 is steady. */
  pulse?: number;
  /** Only show while the control is hovered. */
  onHover?: boolean;
}

/** Four corner brackets — the reticle a camera puts around a face. */
export class BracketsFlare extends Flare {
  color: Color | null;
  size: number;
  thickness: number;
  offset: number;
  pulse: number;
  onHover: boolean;

  /** How much further out the brackets ride at the top of a breath. */
  static readonly BREATH = 3;

  constructor(options: BracketsFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.size = options.size ?? 12;
    this.thickness = options.thickness ?? 2;
    this.offset = options.offset ?? 3;
    this.pulse = options.pulse ?? 0;
    this.onHover = options.onHover ?? false;
  }

  override get overflow(): number {
    return Math.max(0, this.offset) + (this.pulse > 0 ? BracketsFlare.BREATH : 0) + this.thickness;
  }

  override get animated(): boolean {
    return this.pulse > 0;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.onHover && !ctx.state.hovered) return;
    const base = this.color ?? ctx.theme.palette.accent;
    const wave = this.pulse > 0 ? pulseWave(ctx.time, this.pulse) : 1;
    const alpha = this.pulse > 0 ? 0.5 + 0.5 * wave : 1;
    const r = grow(ctx.bounds, this.offset + (this.pulse > 0 ? wave * BracketsFlare.BREATH : 0));
    const ink = fade(base, base.a * alpha);
    // Never let the two brackets on a short edge meet in the middle.
    const arm = Math.min(this.size, Math.min(r.width, r.height) / 2);
    const t = this.thickness;

    for (const dx of [0, 1]) {
      for (const dy of [0, 1]) {
        ctx.rc.fillRect(
          {
            x: dx ? r.x + r.width - arm : r.x,
            y: dy ? r.y + r.height - t : r.y,
            width: arm,
            height: t,
          },
          ink,
        );
        ctx.rc.fillRect(
          {
            x: dx ? r.x + r.width - t : r.x,
            y: dy ? r.y + r.height - arm : r.y,
            width: t,
            height: arm,
          },
          ink,
        );
      }
    }
  }
}

// --- inset --------------------------------------------------------------

export interface InsetFlareOptions {
  color?: Color;
  /** How far the shading reaches in from the edge, px. */
  depth?: number;
  strength?: number;
  radius?: number;
}

/**
 * An inner shadow: the edges darken inwards, so the control reads as a well
 * rather than a tile.
 *
 * `ShadowFlare` turned outside in — hairline outlines stepping inward at
 * falling opacity, which leaves the middle of the control untouched.
 */
export class InsetFlare extends Flare {
  color: Color | null;
  depth: number;
  strength: number;
  radius: number;

  constructor(options: InsetFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.depth = options.depth ?? 6;
    this.strength = options.strength ?? 1;
    this.radius = options.radius ?? -1;
  }

  override paintAbove(ctx: FlareContext): void {
    const base = this.color ?? ctx.theme.palette.shadow;
    if (base.a <= 0) return;
    const radius = this.radius >= 0 ? this.radius : ctx.theme.metrics.controlCornerRadius;
    const steps = Math.max(1, Math.round(this.depth));
    for (let i = 0; i < steps; i++) {
      // Squared falloff: the outermost ring carries most of the weight and the
      // last is barely there, which is what stops the edge reading as a band.
      // The weight is high because the palette's shadow is already faint —
      // a Modern theme hands over black at a fifth opacity.
      const t = 1 - i / steps;
      ctx.rc.strokeRect(
        grow(ctx.bounds, -i),
        fade(base, base.a * this.strength * 0.9 * t * t),
        1,
        Math.max(0, radius - i),
      );
    }
  }
}

// --- orbit --------------------------------------------------------------

export interface OrbitFlareOptions {
  color?: Color;
  count?: number;
  /** Seconds for one lap of the perimeter. */
  period?: number;
  /** Dot size in px. */
  size?: number;
  /** Px outside the edge the dots ride. */
  offset?: number;
  /** Fading dots strung out behind each one. */
  trail?: number;
  /** Only run while the control is hovered. */
  onHover?: boolean;
}

/**
 * Dots chasing each other around the control.
 *
 * Where `SparkleFlare` pins its stars down and twinkles them, these keep
 * their brightness and move — the difference between a decoration and a
 * control that is busy.
 */
export class OrbitFlare extends HoverGatedFlare {
  color: Color | null;
  count: number;
  period: number;
  size: number;
  offset: number;
  trail: number;

  constructor(options: OrbitFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.count = options.count ?? 3;
    this.period = options.period ?? 3;
    this.size = options.size ?? 4;
    this.offset = options.offset ?? 0;
    this.trail = options.trail ?? 4;
  }

  override get overflow(): number {
    return Math.max(0, this.offset) + this.size;
  }

  override get animated(): boolean {
    return this.period > 0 && this.running;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.gated(ctx)) return;
    const base = this.color ?? ctx.theme.palette.accent;
    const r = grow(ctx.bounds, this.offset);
    const total = perimeter(r);
    if (total <= 0 || this.count <= 0) return;

    const lap = this.period > 0 ? (ctx.time / this.period) % 1 : 0;
    const spacing = this.size * 1.6;
    for (let i = 0; i < this.count; i++) {
      const head = (lap + i / this.count) * total;
      for (let k = 0; k <= this.trail; k++) {
        const f = 1 - k / (this.trail + 1);
        const s = this.size * f;
        if (s < 0.5) continue;
        const p = pointOnPerimeter(r, head - k * spacing);
        ctx.rc.fillRect(
          { x: p.x - s / 2, y: p.y - s / 2, width: s, height: s },
          fade(base, base.a * f * f),
          s / 2,
        );
      }
    }
  }
}

// --- stripes ------------------------------------------------------------

export interface StripesFlareOptions {
  color?: Color;
  /** Stripe thickness, px. */
  width?: number;
  /** Space between stripes, px. */
  gap?: number;
  /** Horizontal shift over the control's height; 1 is roughly 45 degrees. */
  slant?: number;
  /** Px per second the pattern travels; 0 is static. */
  speed?: number;
  opacity?: number;
  /** Paint under the control instead of over it. */
  below?: boolean;
}

/**
 * Diagonal hazard stripes, optionally crawling — the barber's pole an
 * indeterminate progress bar wears, available on anything.
 */
export class StripesFlare extends Flare {
  color: Color | null;
  width: number;
  gap: number;
  slant: number;
  speed: number;
  opacity: number;
  below: boolean;

  constructor(options: StripesFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.width = options.width ?? 8;
    this.gap = options.gap ?? 10;
    this.slant = options.slant ?? 1;
    this.speed = options.speed ?? 24;
    this.opacity = options.opacity ?? 0.18;
    this.below = options.below ?? false;
  }

  override get animated(): boolean {
    return this.speed !== 0;
  }

  #paint(ctx: FlareContext): void {
    const b = ctx.bounds;
    const base = this.color ?? ctx.theme.palette.accent;
    const ink = fade(base, base.a * this.opacity);
    const step = Math.max(1, this.width + this.gap);
    // The stripes run past the top and bottom edges so their round caps land
    // outside the clip; what is left inside reads as a straight-cut pattern.
    const overhang = this.width;
    const lean = this.slant * (b.height + overhang * 2);
    const drift = this.speed !== 0 ? (ctx.time * this.speed) % step : 0;
    const margin = Math.abs(lean) / 2 + step;
    const from = b.x - margin + drift;
    const to = b.x + b.width + margin;

    ctx.rc.clipped(b, () => {
      for (let x = from; x <= to; x += step) {
        ctx.rc.drawLine(
          { x: x + lean / 2, y: b.y - overhang },
          { x: x - lean / 2, y: b.y + b.height + overhang },
          this.width,
          ink,
        );
      }
    });
  }

  override paintBelow(ctx: FlareContext): void {
    if (this.below) this.#paint(ctx);
  }

  override paintAbove(ctx: FlareContext): void {
    if (!this.below) this.#paint(ctx);
  }
}

// --- tint ---------------------------------------------------------------

export interface TintFlareOptions {
  color?: Color;
  opacity?: number;
  when?: StateTrigger;
  /** Seconds to ease in and out. 0 snaps. */
  fade?: number;
  radius?: number;
}

/** A flat wash that follows a visual state, easing in and out. */
export class TintFlare extends StateFlare {
  color: Color | null;
  opacity: number;
  radius: number;

  constructor(options: TintFlareOptions = {}) {
    super(options.when ?? "hover", options.fade ?? 0.14);
    this.color = options.color ?? null;
    this.opacity = options.opacity ?? 0.18;
    this.radius = options.radius ?? -1;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.level <= 0) return;
    const base = this.color ?? ctx.theme.palette.accent;
    const radius = this.radius >= 0 ? this.radius : ctx.theme.metrics.controlCornerRadius;
    ctx.rc.fillRect(ctx.bounds, fade(base, base.a * this.opacity * this.level), radius);
  }
}

// --- bevel --------------------------------------------------------------

export interface BevelFlareOptions {
  /** Sunken swaps the lit and shaded sides: a well rather than a tile. */
  sunken?: boolean;
  /** How many rings deep the chamfer goes. */
  rings?: number;
}

/**
 * The two-ring 3D chamfer every control wore in 1995.
 *
 * Drawn from the palette's bevel ramp rather than fixed grays, so it follows
 * whichever theme is up — and being a flare rather than theme chrome, a
 * Modern control can wear one without anybody changing themes.
 */
export class BevelFlare extends Flare {
  sunken: boolean;
  rings: number;

  constructor(options: BevelFlareOptions = {}) {
    super();
    this.sunken = options.sunken ?? false;
    this.rings = options.rings ?? 2;
  }

  override paintAbove(ctx: FlareContext): void {
    const p = ctx.theme.palette;
    // The outer ring is the brightest against the darkest; inner rings step
    // in towards the middle of the ramp, and any beyond the second repeat it.
    const lit = [p.bevelHighlight, p.bevelLight];
    const shade = [p.bevelDarkShadow, p.bevelShadow];
    for (let i = 0; i < Math.max(1, this.rings); i++) {
      const l = lit[Math.min(i, lit.length - 1)]!;
      const d = shade[Math.min(i, shade.length - 1)]!;
      bevelEdge(ctx.rc, grow(ctx.bounds, -i), this.sunken ? d : l, this.sunken ? l : d);
    }
  }
}

// --- underline ----------------------------------------------------------

/** Which way an underline opens out. */
export type UnderlineGrowth = "center" | "left" | "right";

const UNDERLINE_GROWTHS: readonly UnderlineGrowth[] = ["center", "left", "right"];

export interface UnderlineFlareOptions {
  color?: Color;
  thickness?: number;
  when?: StateTrigger;
  /** Seconds to ease in and out. 0 snaps. */
  fade?: number;
  /** Px below the control's bottom edge. */
  offset?: number;
  grow?: UnderlineGrowth;
  /** Fraction of the width the bar covers at full strength. */
  extent?: number;
}

/**
 * A bar that grows in under the control while a state holds — what a tab or
 * a nav link does on hover.
 */
export class UnderlineFlare extends StateFlare {
  color: Color | null;
  thickness: number;
  offset: number;
  grow: UnderlineGrowth;
  extent: number;

  constructor(options: UnderlineFlareOptions = {}) {
    super(options.when ?? "hover", options.fade ?? 0.16);
    this.color = options.color ?? null;
    this.thickness = options.thickness ?? 2;
    this.offset = options.offset ?? 2;
    this.grow = options.grow ?? "center";
    this.extent = options.extent ?? 1;
  }

  override get overflow(): number {
    return Math.max(0, this.offset) + this.thickness;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.level <= 0) return;
    const b = ctx.bounds;
    const base = this.color ?? ctx.theme.palette.accent;
    const full = b.width * this.extent;
    const width = full * this.level;
    const x =
      this.grow === "left"
        ? b.x + (b.width - full) / 2
        : this.grow === "right"
          ? b.x + (b.width + full) / 2 - width
          : b.x + b.width / 2 - width / 2;
    ctx.rc.fillRect(
      { x, y: b.y + b.height + this.offset, width, height: this.thickness },
      base,
      this.thickness / 2,
    );
  }
}

// --- scanlines ----------------------------------------------------------

export interface ScanlinesFlareOptions {
  color?: Color;
  /** Distance between one line and the next, px. */
  gap?: number;
  thickness?: number;
  opacity?: number;
  /** Px per second the pattern rolls down; 0 holds still. */
  speed?: number;
}

/**
 * The dark lines of a CRT, laid over the control.
 *
 * `StripesFlare` on its side would nearly do it, but scanlines want no slant
 * and hairline spacing, and the pair are worth telling apart by name.
 */
export class ScanlinesFlare extends Flare {
  color: Color | null;
  gap: number;
  thickness: number;
  opacity: number;
  speed: number;

  constructor(options: ScanlinesFlareOptions = {}) {
    super();
    this.color = options.color ?? null;
    this.gap = options.gap ?? 3;
    this.thickness = options.thickness ?? 1;
    this.opacity = options.opacity ?? 0.22;
    this.speed = options.speed ?? 0;
  }

  override get animated(): boolean {
    return this.speed !== 0;
  }

  override paintAbove(ctx: FlareContext): void {
    const b = ctx.bounds;
    const base = this.color ?? BLACK;
    const ink = fade(base, base.a * this.opacity);
    const step = Math.max(1, this.gap + this.thickness);
    const roll = this.speed !== 0 ? (ctx.time * this.speed) % step : 0;
    ctx.rc.clipped(b, () => {
      for (let y = b.y - step + roll; y < b.y + b.height; y += step) {
        ctx.rc.fillRect({ x: b.x, y, width: b.width, height: this.thickness }, ink);
      }
    });
  }
}

// --- flames -------------------------------------------------------------

export interface FlamesFlareOptions {
  /** The body of the fire, and what the ramp fades from. */
  color?: Color;
  /** The hot core at the roots. */
  tip?: Color;
  /** Tongues along the edge; 0 spaces them to suit the control. */
  count?: number;
  /** Tallest a tongue reaches, px. */
  height?: number;
  /** Flicker rate. */
  speed?: number;
  /** Joints per tongue. More is smoother and costs a quad each. */
  segments?: number;
  /** How far a tip wanders sideways in px; 0 scales it to the height. */
  sway?: number;
  /** How fast ripples travel up a tongue. */
  rise?: number;
  /** Which edge burns. */
  edge?: FlameEdge;
  onHover?: boolean;
}

/** Which edge a `FlamesFlare` burns from. */
export type FlameEdge = "top" | "bottom";

const FLAME_EDGES: readonly FlameEdge[] = ["top", "bottom"];

/**
 * Fire color `t` of the way up a tongue: the hot core at the root, through
 * the body, to nothing.
 *
 * Real flame is brightest and palest where it meets the fuel and reddens as
 * it cools, so the ramp runs core → body over the first third and body →
 * transparent smoke over the rest. Ending at a *darkened* body rather than a
 * transparent one keeps the tips from turning pink as they fade.
 */
function heat(t: number, core: Color, body: Color): Color {
  // Curved rather than linear over the first stretch: a straight ramp is a
  // third of the way to orange by the time it leaves the root, which loses
  // the pale hot base that says "fire" more than the shape does.
  if (t < 0.35) return mixColor(core, body, Math.pow(t / 0.35, 1.6));
  return mixColor(body, withAlpha(darken(body, 0.45), 0), (t - 0.35) / 0.65);
}

/**
 * Fire licking off one edge.
 *
 * Each tongue is a chain of capsules walked from root to tip, narrowing as it
 * goes and colored from the heat ramp, which is what makes it a flame rather
 * than a triangle: soft edges, a curling shape, and a bright pale base under
 * a red top that fades out instead of stopping.
 *
 * Three things move, on their own clocks. The height flickers on smooth
 * noise, squared, so the fire is mostly low with the occasional tall lick.
 * The sideways offset is sampled with a phase that *scrolls along the height*
 * as time passes, so ripples travel up a tongue and the fire climbs rather
 * than waving in place. And the tips wander further than the roots, because
 * that is what fire does.
 *
 * Painted below the control, so the roots go behind its fill and the tongues
 * appear to come from underneath rather than sit on top.
 */
export class FlamesFlare extends HoverGatedFlare {
  color: Color | null;
  tip: Color | null;
  count: number;
  height: number;
  speed: number;
  segments: number;
  sway: number;
  rise: number;
  edge: FlameEdge;

  constructor(options: FlamesFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.tip = options.tip ?? null;
    this.count = options.count ?? 0;
    this.height = options.height ?? 20;
    this.speed = options.speed ?? 2.6;
    this.segments = options.segments ?? 5;
    this.sway = options.sway ?? 0;
    this.rise = options.rise ?? 1.7;
    this.edge = options.edge ?? "top";
  }

  override get overflow(): number {
    return this.height;
  }

  override paintBelow(ctx: FlareContext): void {
    if (this.gated(ctx) || this.count < 0 || this.height <= 0) return;
    const b = ctx.bounds;
    const body = this.color ?? hex("#ff6a1e");
    const core = this.tip ?? hex("#fff0b8");
    const up = this.edge === "top";
    const edgeY = up ? b.y : b.y + b.height;
    const dir = up ? -1 : 1;
    // Tongues are spaced by how tall the fire is, not by how wide the control
    // is: a fixed count leaves a long button holding a row of separate
    // candles, where fire wants to overlap into one body.
    const count =
      this.count > 0 ? this.count : Math.max(3, Math.round(b.width / (this.height * 0.55)));
    const span = b.width / count;
    const segs = Math.max(2, Math.round(this.segments));
    // A tall fire wanders further than a short one, so the default is a
    // fraction of the height rather than a number of pixels.
    const sway = this.sway > 0 ? this.sway : this.height * 0.5;

    // The bed the tongues rise out of: one gradient quad, hot at the edge and
    // gone a third of the way up. Without it the roots read as separate
    // stalks standing on the border rather than one body of fire.
    const bed = this.height * 0.4;
    ctx.rc.fillGradient(
      { x: b.x, y: up ? edgeY - bed : edgeY, width: b.width, height: bed },
      fade(body, body.a * 0.6),
      withAlpha(darken(body, 0.3), 0),
      up ? GRADIENT_UP : GRADIENT_DOWN,
    );

    for (let i = 0; i < count; i++) {
      const seed = i * 4.7;
      // Smooth noise clusters around the middle, which had every tongue the
      // same height. Two rates, then pushed away from the mean, spreads them
      // into the short ones and the occasional tall lick a fire actually has.
      const mixed =
        noise1(ctx.time * this.speed + seed) * 0.65 +
        noise1(ctx.time * this.speed * 2.7 + seed * 2.4) * 0.35;
      const flick = Math.min(1, Math.max(0, (mixed - 0.5) * 1.9 + 0.5));
      const h = this.height * (0.28 + 0.72 * flick);
      if (h < 2) continue;
      const rootX = b.x + (i + 0.5) * span;
      // Roots overlap their neighbors, and are sized from the fire's full
      // height rather than this tongue's: a short lick is still a thick one,
      // which is what keeps the base a single mass of flame.
      const rootW = Math.min(span * 1.2, this.height * 0.42);

      // The tongue's path. `t` runs 0 at the root to 1 at the tip, and the
      // noise phase walks backwards with time, which sends the wave upward.
      const at = (t: number): Point => ({
        x:
          rootX +
          (noise1(seed * 3.1 + t * 2.6 - ctx.time * this.rise) - 0.5) * sway * Math.pow(t, 1.3),
        y: edgeY + dir * t * h,
      });

      // Twice over: a wide dim haze, then the bright core inside it. One pass
      // alone is either hard-edged or washed out. The haze is drawn at half
      // the detail — nobody can see the joints through it.
      for (const pass of FLAME_PASSES) {
        const n = Math.max(2, Math.round(segs * pass.detail));
        let prev = at(0);
        for (let k = 1; k <= n; k++) {
          const t = k / n;
          const next = at(t);
          const mid = (k - 0.5) / n;
          // A tall lick is a hot one: reading the ramp slightly early keeps
          // the big tongues pale while the low ones stay red.
          const ink = heat(mid * (1.12 - 0.28 * flick), core, body);
          // Taper hard: a flame is a point, and anything gentler leaves a
          // blunt egg. Never below a pixel, though — a capsule thinner than
          // that flickers in and out as the antialiasing loses it.
          const w = Math.max(1, rootW * Math.pow(1 - mid, 1.5) * pass.widen);
          ctx.rc.drawLine(prev, next, w, fade(ink, ink.a * pass.alpha));
          prev = next;
        }
      }
    }
  }
}

/** The passes over one tongue, haze first: width, opacity, share of detail. */
const FLAME_PASSES: readonly { widen: number; alpha: number; detail: number }[] = [
  { widen: 1.8, alpha: 0.22, detail: 0.5 },
  { widen: 0.85, alpha: 1, detail: 1 },
];

// --- arcs ---------------------------------------------------------------

export interface ArcsFlareOptions {
  color?: Color;
  /** Arcs alive at once. */
  count?: number;
  /** Kinks in each arc. */
  segments?: number;
  /** How far an arc strays from the edge, px. */
  reach?: number;
  thickness?: number;
  /** Seconds an arc lasts before it snaps somewhere else. */
  period?: number;
  /** How much of the perimeter one arc spans, px. */
  length?: number;
  onHover?: boolean;
}

/**
 * Electricity crawling around the edge.
 *
 * Each arc is a polyline walked along the perimeter with its middle points
 * kicked out along the normal. Everything is seeded from the tick number, so
 * an arc holds its shape for its whole life and then snaps somewhere else —
 * which is what makes it read as crackling rather than wobbling.
 */
export class ArcsFlare extends HoverGatedFlare {
  color: Color | null;
  count: number;
  segments: number;
  reach: number;
  thickness: number;
  period: number;
  length: number;

  constructor(options: ArcsFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.count = options.count ?? 2;
    this.segments = options.segments ?? 6;
    this.reach = options.reach ?? 9;
    this.thickness = options.thickness ?? 1.5;
    this.period = options.period ?? 0.12;
    this.length = options.length ?? 60;
  }

  override get overflow(): number {
    return this.reach + this.thickness * 2;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.gated(ctx) || this.period <= 0) return;
    const b = ctx.bounds;
    const total = perimeter(b);
    if (total <= 0) return;
    const base = this.color ?? hex("#9fd8ff");
    const tick = Math.floor(ctx.time / this.period);
    // Brightest mid-life, so an arc strikes and dies rather than blinking.
    const alpha = Math.sin(Math.PI * ((ctx.time / this.period) % 1));
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;

    for (let i = 0; i < this.count; i++) {
      const seed = tick * 13.7 + i * 91.3;
      const start = hashUnit(seed) * total;
      const points: Point[] = [];
      for (let s = 0; s <= this.segments; s++) {
        const along = pointOnPerimeter(b, start + (s / this.segments) * this.length);
        // Ends stay pinned to the edge; only the middle is thrown outward.
        const pinned = s === 0 || s === this.segments;
        const kick = pinned ? 0 : (hashUnit(seed + s * 5.3) - 0.5) * 2 * this.reach;
        const nx = along.x - cx;
        const ny = along.y - cy;
        const len = Math.hypot(nx, ny) || 1;
        points.push({ x: along.x + (nx / len) * kick, y: along.y + (ny / len) * kick });
      }
      // A wide dim pass under a thin bright one: the halo an arc has.
      ctx.rc.drawPolyline(points, this.thickness * 3, fade(base, base.a * alpha * 0.25));
      ctx.rc.drawPolyline(points, this.thickness, fade(base, base.a * alpha));
    }
  }
}

// --- embers -------------------------------------------------------------

export interface EmbersFlareOptions {
  color?: Color;
  count?: number;
  /** Px past the top edge the highest ember reaches. */
  rise?: number;
  /** Seconds one ember takes to make the climb. */
  period?: number;
  size?: number;
  /** Lateral sway, px. */
  drift?: number;
  onHover?: boolean;
}

/** Sparks lifting off the control and going out as they climb. */
export class EmbersFlare extends HoverGatedFlare {
  color: Color | null;
  count: number;
  rise: number;
  period: number;
  size: number;
  drift: number;

  constructor(options: EmbersFlareOptions = {}) {
    super(options.onHover ?? false);
    this.color = options.color ?? null;
    this.count = options.count ?? 14;
    this.rise = options.rise ?? 22;
    this.period = options.period ?? 2.4;
    this.size = options.size ?? 5;
    this.drift = options.drift ?? 7;
  }

  override get overflow(): number {
    return this.rise + this.size;
  }

  override paintAbove(ctx: FlareContext): void {
    if (this.gated(ctx) || this.period <= 0) return;
    const b = ctx.bounds;
    const base = this.color ?? hex("#ff9a3c");
    const climb = b.height + this.rise;

    for (let i = 0; i < this.count; i++) {
      const seed = i * 7.13;
      // Each ember runs its own offset through the cycle, so they leave in a
      // stream rather than a wave.
      const t = ((ctx.time / this.period) + hashUnit(seed)) % 1;
      const x = b.x + hashUnit(seed + 2.9) * b.width + Math.sin(t * 5 + i) * this.drift * t;
      const y = b.y + b.height - t * climb;
      // In fast, out slowly: a spark is brightest just after it leaves.
      const alpha = t < 0.12 ? t / 0.12 : 1 - (t - 0.12) / 0.88;
      const s = this.size * (1 - t * 0.4);
      if (s < 0.5 || alpha <= 0) continue;
      ctx.rc.fillRect(
        { x: x - s / 2, y: y - s / 2, width: s, height: s },
        fade(base, base.a * alpha),
        s / 2,
      );
    }
  }
}

// --- burst --------------------------------------------------------------

export interface BurstFlareOptions {
  color?: Color;
  /** Rays in the star. */
  rays?: number;
  /** How far a ray travels, px. */
  reach?: number;
  thickness?: number;
  duration?: number;
  /** Turns of spin over one burst's life. */
  spin?: number;
  /** Fire automatically whenever the control is clicked. */
  onClick?: boolean;
}

/**
 * A starburst thrown outward from the middle: a level-up, a critical hit, a
 * reward landing.
 *
 * `RippleFlare`'s transient sibling — same fire-and-retire lifecycle, rays
 * instead of a ring.
 */
export class BurstFlare extends FiredFlare {
  color: Color | null;
  rays: number;
  reach: number;
  thickness: number;
  spin: number;

  constructor(options: BurstFlareOptions = {}) {
    super(options.duration ?? 0.55, options.onClick ?? true);
    this.color = options.color ?? null;
    this.rays = options.rays ?? 10;
    this.reach = options.reach ?? 26;
    this.thickness = options.thickness ?? 2.5;
    this.spin = options.spin ?? 0.12;
  }

  override get overflow(): number {
    return this.reach + this.thickness;
  }

  override paintAbove(ctx: FlareContext): void {
    const base = this.color ?? hex("#ffd23f");
    const b = ctx.bounds;
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;

    for (const t of this.shots(ctx.time)) {
      // Out fast, then coast: the shape of anything thrown.
      const ease = 1 - (1 - t) * (1 - t);
      const outer = this.reach * ease;
      const inner = outer * 0.45;
      const ink = fade(base, base.a * (1 - t));
      const width = this.thickness * (1 - t * 0.7);
      if (width <= 0.1) continue;
      for (let i = 0; i < this.rays; i++) {
        const a = (i / this.rays + t * this.spin) * Math.PI * 2;
        const dx = Math.cos(a);
        const dy = Math.sin(a);
        ctx.rc.drawLine(
          { x: cx + dx * inner, y: cy + dy * inner },
          { x: cx + dx * outer, y: cy + dy * outer },
          width,
          ink,
        );
      }
    }
  }
}

// --- holo ---------------------------------------------------------------

export interface HoloFlareOptions {
  /** Ring thickness, px. */
  thickness?: number;
  /** Px the ring sits outside the control. */
  offset?: number;
  /** Seconds for one turn of the wheel; negative runs it backwards. */
  period?: number;
  /** Steps around the ring. More is smoother and costs more quads. */
  segments?: number;
  /** How many turns of hue fit around the perimeter. */
  spread?: number;
  opacity?: number;
}

/**
 * A rainbow border cycling around the control — the holographic edge a game
 * puts on the rare drop.
 *
 * The gradient primitive interpolates between two stops along one axis, which
 * cannot follow a perimeter, so this walks the edge in short runs and gives
 * each its own hue.
 */
export class HoloFlare extends Flare {
  thickness: number;
  offset: number;
  period: number;
  segments: number;
  spread: number;
  opacity: number;

  constructor(options: HoloFlareOptions = {}) {
    super();
    this.thickness = options.thickness ?? 2;
    this.offset = options.offset ?? 1;
    this.period = options.period ?? 3;
    this.segments = options.segments ?? 36;
    this.spread = options.spread ?? 1;
    this.opacity = options.opacity ?? 1;
  }

  override get overflow(): number {
    return Math.max(0, this.offset) + this.thickness;
  }

  override get animated(): boolean {
    return this.period !== 0;
  }

  override paintAbove(ctx: FlareContext): void {
    const r = grow(ctx.bounds, this.offset);
    const total = perimeter(r);
    const steps = Math.max(3, Math.round(this.segments));
    if (total <= 0) return;
    const turn = this.period !== 0 ? ctx.time / this.period : 0;
    const run = total / steps;
    for (let i = 0; i < steps; i++) {
      const hue = (i / steps) * this.spread + turn;
      const ink = fade(hueRgb(hue), this.opacity);
      // Runs overlap by a hair, or the seams show as gaps on the diagonal.
      strokePerimeterRun(ctx.rc, r, i * run, (i + 1) * run + 0.5, this.thickness, ink);
    }
  }
}

// --- registry -----------------------------------------------------------

/**
 * The built-in kinds, registered under stable names so a flare can be written
 * as data. An application adds its own with `registerFlare`, and can replace
 * any of these by re-registering the same name.
 */
registerFlare("outline", (s: FlareSpec) => {
  const c = color(s, "color");
  return new OutlineFlare({
    ...(c ? { color: c } : {}),
    width: num(s, "width", 2),
    offset: num(s, "offset", 2),
    radius: num(s, "radius", -1),
    pulse: num(s, "pulse", 0),
  });
});

registerFlare("shadow", (s: FlareSpec) => {
  const c = color(s, "color");
  return new ShadowFlare({
    ...(c ? { color: c } : {}),
    offsetX: num(s, "offsetX", 0),
    offsetY: num(s, "offsetY", 3),
    blur: num(s, "blur", 8),
    radius: num(s, "radius", -1),
  });
});

registerFlare("glow", (s: FlareSpec) => {
  const c = color(s, "color");
  return new GlowFlare({
    ...(c ? { color: c } : {}),
    spread: num(s, "spread", 8),
    strength: num(s, "strength", 1),
    radius: num(s, "radius", -1),
    pulse: num(s, "pulse", 0),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("gradient", (s: FlareSpec) => {
  const from = color(s, "from");
  const to = color(s, "to");
  return new GradientFlare({
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    direction: choice(s, "direction", GRADIENT_DIRECTIONS, "down"),
    radius: num(s, "radius", -1),
    shift: num(s, "shift", 0),
    above: bool(s, "above", false),
    opacity: num(s, "opacity", 1),
  });
});

registerFlare("sparkle", (s: FlareSpec) => {
  const c = color(s, "color");
  return new SparkleFlare({
    ...(c ? { color: c } : {}),
    count: num(s, "count", 8),
    period: num(s, "period", 2.2),
    size: num(s, "size", 5),
    inside: bool(s, "inside", false),
    onHover: bool(s, "onHover", false),
    seed: num(s, "seed", 1),
  });
});

const ripple = (s: FlareSpec, inward: boolean): RippleFlare => {
  const c = color(s, "color");
  return new RippleFlare({
    ...(c ? { color: c } : {}),
    duration: num(s, "duration", 0.45),
    reach: num(s, "reach", 14),
    width: num(s, "width", 2),
    inward: bool(s, "inward", inward),
    onClick: bool(s, "onClick", true),
  });
};

registerFlare("ripple", (s: FlareSpec) => ripple(s, false));

// The same effect with the travel reversed. A kind of its own rather than
// only an option, so it can be picked by name from a list of kinds.
registerFlare("implode", (s: FlareSpec) => ripple(s, true));

registerFlare("sheen", (s: FlareSpec) => {
  const c = color(s, "color");
  return new SheenFlare({
    ...(c ? { color: c } : {}),
    width: num(s, "width", 18),
    duration: num(s, "duration", 0.7),
    period: num(s, "period", 3.4),
    slant: num(s, "slant", 0.4),
    opacity: num(s, "opacity", 0.35),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("ants", (s: FlareSpec) => {
  const c = color(s, "color");
  return new AntsFlare({
    ...(c ? { color: c } : {}),
    dash: num(s, "dash", 6),
    gap: num(s, "gap", 4),
    thickness: num(s, "thickness", 1),
    offset: num(s, "offset", 2),
    speed: num(s, "speed", 14),
  });
});

registerFlare("brackets", (s: FlareSpec) => {
  const c = color(s, "color");
  return new BracketsFlare({
    ...(c ? { color: c } : {}),
    size: num(s, "size", 12),
    thickness: num(s, "thickness", 2),
    offset: num(s, "offset", 3),
    pulse: num(s, "pulse", 0),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("inset", (s: FlareSpec) => {
  const c = color(s, "color");
  return new InsetFlare({
    ...(c ? { color: c } : {}),
    depth: num(s, "depth", 6),
    strength: num(s, "strength", 1),
    radius: num(s, "radius", -1),
  });
});

registerFlare("orbit", (s: FlareSpec) => {
  const c = color(s, "color");
  return new OrbitFlare({
    ...(c ? { color: c } : {}),
    count: num(s, "count", 3),
    period: num(s, "period", 3),
    size: num(s, "size", 4),
    offset: num(s, "offset", 0),
    trail: num(s, "trail", 4),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("stripes", (s: FlareSpec) => {
  const c = color(s, "color");
  return new StripesFlare({
    ...(c ? { color: c } : {}),
    width: num(s, "width", 8),
    gap: num(s, "gap", 10),
    slant: num(s, "slant", 1),
    speed: num(s, "speed", 24),
    opacity: num(s, "opacity", 0.18),
    below: bool(s, "below", false),
  });
});

registerFlare("tint", (s: FlareSpec) => {
  const c = color(s, "color");
  return new TintFlare({
    ...(c ? { color: c } : {}),
    opacity: num(s, "opacity", 0.18),
    when: choice(s, "when", STATE_TRIGGERS, "hover"),
    fade: num(s, "fade", 0.14),
    radius: num(s, "radius", -1),
  });
});

registerFlare("bevel", (s: FlareSpec) => {
  return new BevelFlare({
    sunken: bool(s, "sunken", false),
    rings: num(s, "rings", 2),
  });
});

registerFlare("underline", (s: FlareSpec) => {
  const c = color(s, "color");
  return new UnderlineFlare({
    ...(c ? { color: c } : {}),
    thickness: num(s, "thickness", 2),
    when: choice(s, "when", STATE_TRIGGERS, "hover"),
    fade: num(s, "fade", 0.16),
    offset: num(s, "offset", 2),
    grow: choice(s, "grow", UNDERLINE_GROWTHS, "center"),
    extent: num(s, "extent", 1),
  });
});

registerFlare("scanlines", (s: FlareSpec) => {
  const c = color(s, "color");
  return new ScanlinesFlare({
    ...(c ? { color: c } : {}),
    gap: num(s, "gap", 3),
    thickness: num(s, "thickness", 1),
    opacity: num(s, "opacity", 0.22),
    speed: num(s, "speed", 0),
  });
});

registerFlare("flames", (s: FlareSpec) => {
  const c = color(s, "color");
  const tip = color(s, "tip");
  return new FlamesFlare({
    ...(c ? { color: c } : {}),
    ...(tip ? { tip } : {}),
    count: num(s, "count", 9),
    height: num(s, "height", 18),
    speed: num(s, "speed", 2.6),
    segments: num(s, "segments", 5),
    sway: num(s, "sway", 9),
    rise: num(s, "rise", 1.7),
    edge: choice(s, "edge", FLAME_EDGES, "top"),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("arcs", (s: FlareSpec) => {
  const c = color(s, "color");
  return new ArcsFlare({
    ...(c ? { color: c } : {}),
    count: num(s, "count", 2),
    segments: num(s, "segments", 6),
    reach: num(s, "reach", 9),
    thickness: num(s, "thickness", 1.5),
    period: num(s, "period", 0.12),
    length: num(s, "length", 60),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("embers", (s: FlareSpec) => {
  const c = color(s, "color");
  return new EmbersFlare({
    ...(c ? { color: c } : {}),
    count: num(s, "count", 14),
    rise: num(s, "rise", 22),
    period: num(s, "period", 2.4),
    size: num(s, "size", 5),
    drift: num(s, "drift", 7),
    onHover: bool(s, "onHover", false),
  });
});

registerFlare("burst", (s: FlareSpec) => {
  const c = color(s, "color");
  return new BurstFlare({
    ...(c ? { color: c } : {}),
    rays: num(s, "rays", 10),
    reach: num(s, "reach", 26),
    thickness: num(s, "thickness", 2.5),
    duration: num(s, "duration", 0.55),
    spin: num(s, "spin", 0.12),
    onClick: bool(s, "onClick", true),
  });
});

registerFlare("holo", (s: FlareSpec) => {
  return new HoloFlare({
    thickness: num(s, "thickness", 2),
    offset: num(s, "offset", 1),
    period: num(s, "period", 3),
    segments: num(s, "segments", 36),
    spread: num(s, "spread", 1),
    opacity: num(s, "opacity", 1),
  });
});
